import { RadioGroup } from "@headlessui/react";
import { useAtom } from "jotai";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useIsAnonymousUser } from "@/hooks/useIsAnonymousUser";
import { useIsTariffChange } from "@/hooks/useIsTariffChange";
import { DataContext } from "@/provider/DataContextProvider";
import { useGetCustomerTribe, useGetDetailTribe } from "@/services/api";
import { onboardingAnalytics } from "@/utils/analytics/onboardingAnalytics";
import { accessTokenAtom } from "@/utils/atoms";
import {
  TransformedProduct,
  useGetPrechargedOptionForBasket,
} from "@/utils/tariffUtils";

import { MobileAboCard } from "./MobileAboCard";

export const MobileAboForm = ({
  value,
  setValue,
  mobileAbos,
  showCustomRateTab,
  isCustomRateTabLinkDisabled,
  currentTariffIsFlatMobile,
}: {
  value: string | undefined;
  setValue: (value: string) => void;
  mobileAbos: TransformedProduct[];
  showCustomRateTab: () => void;
  isCustomRateTabLinkDisabled?: boolean;
  currentTariffIsFlatMobile?: boolean;
}) => {
  const { t } = useTranslation();
  const prechargedProduct = usePrechargedProductIfBasketExists();
  const [isAnonymousUser] = useIsAnonymousUser();

  const [accessToken] = useAtom(accessTokenAtom);
  const { data: customerTribe } = useGetCustomerTribe({
    query: {
      enabled: !isAnonymousUser,
    },
  });
  const { data: tribeData } = useGetDetailTribe(customerTribe?.tribeId ?? "", {
    query: {
      enabled: !!accessToken && !!customerTribe?.tribeId,
    },
  });

  useEffect(() => {
    onboardingAnalytics.pushProductImpression(
      mobileAbos,
      "flat-mobile-selection",
    );
    // We want to push the product impression only once, so we don't need to run this effect again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mt-8 flex flex-col">
      <RadioGroup value={value ?? ""} onChange={setValue}>
        <div className="-mx-10 flex flex-wrap justify-center gap-6">
          {mobileAbos.map((product) => (
            <MobileAboCard
              key={product.id}
              value={value}
              product={product}
              tribeDiscount={
                currentTariffIsFlatMobile
                  ? tribeData?.tribeDiscount
                  : tribeData?.tribeDiscountNext
              }
              disabled={
                prechargedProduct !== undefined &&
                prechargedProduct.id !== product.id
              }
            />
          ))}
        </div>
      </RadioGroup>
      {isCustomRateTabLinkDisabled || (
        <div className="mt-8 w-full text-center">
          <button
            className="text-md text-primary-100 underline"
            onClick={showCustomRateTab}
          >
            {t("portal:mobileAbo.toDataOnlyTariffs")}
          </button>
        </div>
      )}
    </div>
  );
};

const useDataContext = () => {
  const { dataContext } = useContext(DataContext);

  return dataContext;
};

const usePrechargedProductIfBasketExists = () => {
  const isTariffChange = useIsTariffChange();
  const { basket } = useDataContext();

  return useGetPrechargedOptionForBasket(isTariffChange ? undefined : basket);
};
